import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { CalendarEventCard } from './CalendarEventCard';
import type { CalendarEvent } from '../types';

interface CalendarShowMoreProps {
  count: number;
  remainingEvents: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}

export function CalendarShowMore({ count, remainingEvents, onEventClick }: CalendarShowMoreProps) {
  const { t } = useTranslation('calendar');
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  function handleMouseEnter() {
    clearTimeout(closeTimeoutRef.current);
    setOpen(true);
  }

  function handleMouseLeave() {
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  }

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full rounded px-1 py-0.5 text-left text-[10px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          data-testid="calendar-show-more-trigger"
          aria-label={t('showMore', { count })}
        >
          {t('showMore', { count })}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-56 p-2"
        side="right"
        align="start"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid="calendar-show-more-popover"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1">
          {remainingEvents.map((event) => (
            <div key={event.id} className="h-6">
              <CalendarEventCard
                event={event}
                onClick={() => {
                  setOpen(false);
                  onEventClick(event);
                }}
              />
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
